import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { config } from '@accounts/config';
import { Accounts, Family, Handoff, Inbox, Legal, Organizations, Return, type Identity, type Sender } from '@accounts/domain';
import { type Language, Selection } from '@accounts/locale';
import { type Choice, device } from '@accounts/theme';
import { Session } from '@beyond-js/ui/dom';
import { usePreferences } from '@beyond-js/ui/react';
import { Client, Provider, Refusal } from '@accounts/transport';
import { Keeper, type Kept } from './keeper';

/** How far the application has got in working out who is here */
export type Standing = 'reading' | 'in' | 'out' | 'unavailable';

interface Value {
	standing: Standing;
	identity?: Identity;
	/** Why nobody is signed in, when that is a refusal rather than simply not having started */
	refusal?: Refusal;
	/**
	 * Why a person is asked to sign in again, as a catalog key: a session that expired or was ended, or
	 * an account that may not sign in. Nothing when nobody was signed in, or when they signed out here
	 */
	ended?: string;
	/** The transport, for a domain class of a screen's own (`new Factors(client)`) */
	client: Client;
	accounts: Accounts;
	organizations: Organizations;
	handoff: Handoff;
	/** The person's notifications, relayed to Projects by this service */
	inbox: Inbox;
	/** What the family bar shows, relayed to Projects the same way */
	family: Family;
	/** The product that sent the person here, once the service has validated the way back to it */
	sender?: Sender;
	/** Reads a return request out of an address, validates it, and keeps only the validated answer */
	arrive: (parameters: URLSearchParams) => Promise<Sender | undefined>;
	selection: Selection;
	/** The appearance in effect: the account's once read, this browser's copy before that */
	appearance: Choice;
	language: Language;
	/**
	 * Re-reads the identity document. Every screen that changes standing calls this. A session that
	 * ended meanwhile is the page's session's to answer (`Keeper`): the person stays where they were.
	 *
	 * @returns Whether it was read. When it was not because Accounts did not answer, a person who was
	 * signed in stays signed in with what was last read, and the screen says it may be out of date.
	 */
	refresh: () => Promise<boolean>;
	/**
	 * Reads who is here after the person ended their own session in this tab (signing out, the local
	 * switch, a recent sign-in asked for): nobody leads to the signed-out screens, without a dialog.
	 *
	 * @param explain Whether the sign-in screen says the session ended (after signing out on all
	 * devices); signing out here needs no explanation
	 */
	settle: (explain?: boolean) => Promise<boolean>;
	/** How many times the session was renewed on this page: a screen reads its view again when it changes */
	renewed: number;
	/** What a destructive action that met an ended session says beside itself: it was not sent */
	unsent: string;
	/**
	 * Why the last language or appearance change applied on this device did not reach the account, so
	 * the screen says so rather than leaving another device on the old value (ACC-13); none once saved
	 */
	unsaved?: Refusal;
	/** Changes the language, for the account when someone is signed in, else for this browser */
	choose: (language: Language) => Promise<void>;
	/** Changes the appearance the same way */
	look: (choice: Choice) => Promise<void>;
	text: (key: string, values?: Record<string, string | number>) => string;
	date: (value: string | Date | undefined) => string;
}

const Context = createContext<Value | undefined>(undefined);

/**
 * Who is here, in what language, and looking how.
 *
 * The three are together on purpose. A person's language and appearance are properties of their
 * account, so they arrive with the identity document and have to be applied the moment it does;
 * keeping them in separate providers produced a visible flash of the wrong language on every reload.
 *
 * Before an account is known the browser's own preferences apply, so the sign-in screen is already in
 * the right language for someone who has never signed in.
 */
export function Presence({ children }: { children: ReactNode }) {
	const client = useMemo(() => new Client(config.authority), []);
	const provider = useMemo(() => new Provider(config.provider, config.key), []);
	const accounts = useMemo(() => new Accounts(client, provider, new Legal(config.legal)), [client, provider]);
	const organizations = useMemo(() => new Organizations(client), [client]);
	const handoff = useMemo(() => new Handoff(client), [client]);
	const back = useMemo(() => new Return(client), [client]);

	const preferences = device.preferences;
	const selection = useMemo(() => {
		const selection = new Selection(Selection.preferred(device.remembered));
		// The helper paints `lang` and keeps the copy: it starts from what this screen is written in
		if (preferences.locale !== selection.language) preferences.choose({ locale: selection.language });
		return selection;
	}, [preferences]);
	const inbox = useMemo(() => new Inbox(client, () => selection.language), [client, selection]);
	const family = useMemo(() => new Family(client, () => accounts.providers()), [client, accounts]);

	const [standing, setStanding] = useState<Standing>('reading');
	const [identity, setIdentity] = useState<Identity>();
	const [refusal, setRefusal] = useState<Refusal>();
	const [ended, setEnded] = useState<string>();
	const [sender, setSender] = useState<Sender | undefined>(() => back.current);
	const [language, setLanguage] = useState<Language>(selection.language);
	const { appearance } = usePreferences(preferences);

	// What was last read, for a re-read that gets no answer: it keeps the person where they were
	const held = useRef<Identity>();
	// The page's session for the person signed in
	const [kept, setKept] = useState<Kept>({ session: null });
	const [renewed, setRenewed] = useState(0);
	const [unsaved, setUnsaved] = useState<Refusal>();

	const read = useCallback(async (ended: 'session' | 'caller', explain: boolean) => {
		try {
			const found = await accounts.identity({ ended });
			held.current = found;
			setIdentity(found);
			setRefusal(undefined);
			setEnded(undefined);
			setStanding('in');

			// The account's own preferences win over what this browser remembered and replace its copy;
			// an appearance never chosen (null) follows the system, Accounts' default
			preferences.apply({ appearance: found.account.appearance, locale: found.account.locale });
			return true;
		} catch (error) {
			const given = Refusal.is(error) ? error : new Refusal('INTERNAL', 'Unexpected');
			// The page's session withheld it and answers the person itself: what was read stays
			if (given.quiet) return false;
			// Only a verdict about the identity signs a person out. Anything else — no answer, a server
			// error, an answer this screen did not expect — is Accounts not answering, and must not be
			// shown as a signed-out person
			const out = given.unauthenticated || given.denied || given.status === 401 || given.status === 403;
			setRefusal(given);
			if (!out && held.current) return false;
			// A session that was here and is not any more ended, even when the answer is only that nobody
			// is signed in (it was ended in another tab, or its cookie went)
			if (out) setEnded(!explain ? undefined : given.ended ? given.key : held.current ? 'state.revoked' : undefined);
			held.current = undefined;
			setIdentity(undefined);
			setStanding(out ? 'out' : 'unavailable');
			// The way back belonged to the visit of whoever was signed in. It was validated for them,
			// and the next person to sign in on this tab did not come from anywhere
			if (out) {
				back.forget();
				setSender(undefined);
			}
			return false;
		}
	}, [accounts, back, preferences]);
	const refresh = useCallback(() => read('session', true), [read]);
	const settle = useCallback((explain = false) => read('caller', explain), [read]);

	// The transport gives an answer that says the session ended to the page's session while there is one
	useEffect(() => {
		client.session = kept.session;
		return () => {
			if (client.session === kept.session) client.session = null;
		};
	}, [client, kept.session]);
	const renew = useCallback(() => {
		setRenewed(count => count + 1);
		void refresh();
	}, [refresh]);

	const arrive = useCallback(
		async (parameters: URLSearchParams) => {
			const validated = await back.arrive(parameters);
			setSender(validated);
			return validated;
		},
		[back]
	);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	useEffect(() => selection.listen(setLanguage), [selection]);
	// The helper holds the language in effect; the catalog follows it
	useEffect(() => preferences.subscribe(({ locale }) => selection.select(locale as Language)), [preferences, selection]);

	/**
	 * This is where the account's values are changed. The change applies at once on this device, and
	 * for a signed-in person it is written to the account and what the service answered is applied, so
	 * every product finds it on the person's next arrival. Without an answer the device keeps the choice.
	 */
	const prefer = useCallback(
		async (values: { locale?: Language; appearance?: Choice }) => {
			preferences.choose(values);
			if (!identity) return;
			try {
				const saved = await accounts.prefer(values);
				setUnsaved(undefined);
				preferences.apply({ appearance: saved.account.appearance, locale: saved.account.locale });
			} catch (error) {
				// The device keeps the choice; the screen says it did not reach the account
				const given = Refusal.is(error) ? error : new Refusal('INTERNAL', 'Unexpected');
				if (!given.quiet) setUnsaved(given);
			}
		},
		[accounts, identity, preferences]
	);
	const choose = useCallback((locale: Language) => prefer({ locale }), [prefer]);
	const look = useCallback((appearance: Choice) => prefer({ appearance }), [prefer]);

	const value = useMemo<Value>(
		() => ({
			standing,
			identity,
			refusal,
			ended,
			client,
			accounts,
			organizations,
			handoff,
			inbox,
			family,
			sender,
			arrive,
			selection,
			appearance,
			language,
			refresh,
			settle,
			renewed,
			unsaved,
			unsent: kept.session?.text('unsent') ?? String(Session.labels[language].unsent),
			choose,
			look,
			text: (key, values) => selection.catalog.text(key, values),
			date: value => selection.date(value)
		}),
		[standing, identity, refusal, ended, client, accounts, organizations, handoff, inbox, family, sender, arrive, selection, appearance, language, refresh, settle, renewed, unsaved, kept, choose, look]
	);

	return (
		<Context.Provider value={value}>
			{identity ? <Keeper key={`${identity.account.id}:${language}`} identity={identity} accounts={accounts} language={language} onmade={setKept} onrenewed={renew} /> : null}
			{children}
		</Context.Provider>
	);
}

/**
 * @throws Error when used outside the provider, which is a programming mistake rather than a state
 */
export function usePresence(): Value {
	const value = useContext(Context);
	if (!value) throw new Error('usePresence must be used inside Presence');
	return value;
}
